import { Colors, getRouteTextColor } from './colors'

// Common UI frame styles and helper functions for dark panels and cards.

/** Returns panel frame style string with top or full rounded corners. */
export function panelFrame(sides: 'all' | 'top' = 'all'): string {
  return sides === 'top'
    ? 'border-radius: 18px 18px 0 0; border: none;'
    : `border-radius: 18px; border: 1px solid ${Colors.borderStrong};`
}

/** Returns panel title typography style string. */
export function panelTitle(fontSize = 22): string {
  return [
    `font-family: var(--bjl-font-heading, var(--bjl-font-body))`,
    `color: ${Colors.text}`,
    `font-size: ${fontSize}px`,
    `font-weight: var(--bjl-font-weight-bold, 700)`,
    `letter-spacing: -0.01em`,
  ].join('; ')
}

/** Returns card background and border-radius style string. */
export function cardChrome(radius = 14): string {
  return `background-color: ${Colors.surface}; border-radius: ${radius}px;`
}

/** Returns chip button style string based on state. */
export function chipChrome(state: 'off' | 'outlined' | 'on' = 'off'): string {
  const base = 'display: inline-flex; align-items: center; gap: 5px;'
    + ' border-radius: 8px; padding: 7px 12px; white-space: nowrap;'
    + ' font-size: var(--bjl-font-size-xs); font-weight: 700; line-height: 1.2;'
  if (state === 'on') {
    return `${base} background-color: ${Colors.text}; color: #000; border: 1px solid ${Colors.text};`
  }
  if (state === 'outlined') {
    return `${base} background-color: ${Colors.surfaceElevated}; color: ${Colors.text};`
      + ` border: 1px solid ${Colors.borderStrong};`
  }
  return `${base} background-color: ${Colors.surfaceElevated}; color: ${Colors.textSecondary};`
    + ' border: 1px solid transparent;'
}

/** Row separator border style. */
export const ROW_RULE = `1px solid ${Colors.border}`

/**
 * The ‹ that pops a drill-down level — one size for every sheet that has levels, so the
 * back control does not change size as a rider moves between them.
 *
 * The glyph IS the icon here, so `font-size` is its size, and since the bjl Button's own
 * padding is untouched by it the same line grows the box. That alone is what the sheets used
 * to do, and at the Button default (16px body) it left a ~28px target — half the 44px a
 * thumb needs, and the reason back was the hardest control on the sheet to hit. The explicit
 * minimums are the fix; the flex centring is what keeps the glyph in the middle of the larger
 * box rather than riding its baseline.
 */
export const BACK_ARROW_CHROME =
  'flex-shrink: 0; font-size: 28px; line-height: 1; ' +
  'min-width: 44px; min-height: 44px; ' +
  'display: inline-flex; align-items: center; justify-content: center;'

/** Floating map button style. */
export const tileButtonChrome =
  `background-color: ${Colors.surfaceElevated}; ` +
  `border: 1px solid ${Colors.borderStrong}; ` +
  `box-shadow: var(--bjl-shadow-sm, 0 1px 3px rgba(0,0,0,0.6));`

/** Translucent background color for map overlays. */
export const GLASS_BG = 'rgba(0,0,0,0.92)'

// LED rollsign style tokens.

/** Amber: destination, stop names, the sign's own voice. */
export const LED_AMBER = '#FFB100'
/** Green: times and confirmations. */
export const LED_GREEN = '#39FF6E'
/** Red: the next stop, the one thing the rider needs right now. */
export const LED_RED = '#FF3B30'
/** Off-white: the rail the stop strip hangs from, and already-passed clock times. */
export const LED_RAIL = '#cfd4dd'

/** Returns text-shadow glow style for LED text. */
export function ledGlow(color: string, size = 8, alpha = 0.6): string {
  const rgb = color === LED_AMBER
    ? '255,177,0'
    : color === LED_GREEN
      ? '57,255,110'
      : '255,59,48'
  return `color: ${color}; text-shadow: 0 0 ${size}px rgba(${rgb},${alpha});`
}

/** CRT scanline background pattern style. */
export const LED_SCANLINES =
  'background: repeating-linear-gradient(0deg,rgba(255,255,255,.028) 0 1px,transparent 1px 3px);'

/** LED rollsign font stack. */
export const LED_FONT = "'VT323', 'Courier New', ui-monospace, monospace"

// ---------------------------------------------------------------------------
// Typography scale
// ---------------------------------------------------------------------------
// One definition of "how big / how heavy" per role of text, on the ui-themes
// --bjl-font-size-* token scale (xs 12 to 3xl 36 at a 16px root) rather than ad-hoc literals.
//
// 12px (xs) is the app's readability FLOOR; nothing renders below it. Secondary text carries
// at least a 600 weight so it stays legible small. The sizes are rem tokens, so a rider's OS
// font-scaling scales the whole app.
//
// Each role returns a style STRING to compose into a component's existing `style="…"`
// alongside its color. Drop the surrounding string's own font-size/weight when adopting one.
function role(size: string, weight: 'medium' | 'semibold' | 'bold', lh: 'tight' | 'normal'): string {
  const fallback = weight === 'bold' ? 700 : weight === 'semibold' ? 600 : 500
  return [
    `font-size: var(--bjl-font-size-${size})`,
    `font-weight: var(--bjl-font-weight-${weight}, ${fallback})`,
    `line-height: var(--bjl-line-height-${lh})`,
  ].join('; ')
}

// ---------------------------------------------------------------------------
// Fitting type to a box
// ---------------------------------------------------------------------------
// One canvas for every measurement this module makes. Created on first use and kept: a 2D
// context is cheap to reuse and not cheap to allocate per keystroke of a changing headline.
let measureCtx: CanvasRenderingContext2D | null | undefined

/** Calculates font size in pixels to fit text within available width. */
export function fitFontPx(
  text: string,
  fontFamily: string,
  weight: number,
  availablePx: number,
  maxPx: number,
  minPx: number,
  trackingEm = 0,
): number {
  // availablePx is 0 on the first render, before the element has been laid out and the
  // `clientWidth` binding has fired. Start at full size and let the binding correct it:
  // starting at minPx would flash the headline small on every boarding.
  if (!text || availablePx <= 0) return maxPx

  if (measureCtx === undefined) {
    measureCtx =
      typeof document !== 'undefined'
        ? document.createElement('canvas').getContext('2d')
        : null
  }
  if (!measureCtx) return maxPx

  // Advance width is linear in font size, so one measurement at a reference size gives the
  // width at every size: em width × px = px width.
  const REF_PX = 100
  measureCtx.font = `${weight} ${REF_PX}px ${fontFamily}`
  const emWidth = measureCtx.measureText(text).width / REF_PX + trackingEm * text.length
  if (emWidth <= 0) return maxPx

  return Math.max(minPx, Math.min(maxPx, Math.floor(availablePx / emWidth)))
}

export const BULLET_FONT_FAMILY = `'TeX Gyre Heros', 'Helvetica Neue', Helvetica, Arial, sans-serif`

/** Bullet font family and weight style. */
export const BULLET_TYPE = `font-family: ${BULLET_FONT_FAMILY}; font-weight: 700`

/** Returns full text style for route bullets. */
export function bulletText(routeId: string, px: number): string {
  return (
    `color: ${getRouteTextColor(routeId)}; ${BULLET_TYPE};`
    + ` font-size: ${px}px; line-height: 1; white-space: nowrap`
  )
}

/** Returns canvas font string for route bullets. */
export function canvasBulletFont(px: number): string {
  return `700 ${px}px ${BULLET_FONT_FAMILY}`
}

const BULLET_FIT_CIRCLE = 0.78
const BULLET_FIT_DIAMOND = 0.56
const BULLET_FIT_RIM = 0.94
const BULLET_MIN_PX = 6

const bulletFitCache = new Map<string, number>()

/** Calculates font size for bullet label to fit within disc or diamond. */
export function bulletFontPx(
  label: string,
  boxPx: number,
  maxPx: number,
  shape: 'circle' | 'diamond' = 'circle',
): number {
  const key = `${label}|${boxPx}|${maxPx}|${shape}`
  const hit = bulletFitCache.get(key)
  if (hit !== undefined) return hit
  const band = boxPx * (shape === 'diamond' ? BULLET_FIT_DIAMOND : BULLET_FIT_CIRCLE)
  // Two fits, and the tighter wins: the first is the size the label WANTS (inside the band,
  // never below the readable floor), the second the size it MAY have (inside the rim, floored
  // at 1px because a label that cannot make the floor still may not cross the edge).
  const wanted = fitFontPx(label, BULLET_FONT_FAMILY, 700, band, maxPx, BULLET_MIN_PX)
  const allowed = fitFontPx(label, BULLET_FONT_FAMILY, 700, boxPx * BULLET_FIT_RIM, maxPx, 1)
  const px = Math.min(wanted, allowed)
  bulletFitCache.set(key, px)
  return px
}

/** Promise resolving when TeX Gyre Heros bullet font is loaded. */
export function bulletFontReady(timeoutMs = 2000): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts) return Promise.resolve()
  return Promise.race([
    document.fonts.load(`700 16px 'TeX Gyre Heros'`),
    new Promise((resolve) => setTimeout(resolve, timeoutMs)),
  ]).then(() => undefined, () => undefined)
}

/** Bus LCD green kicker color. */
export const LCD_GREEN = '#25E07A'
/** Bus LCD stop rail and dot color. */
export const LCD_DOT = '#1E9BD7'
/** Bus LCD next stop name color. */
export const LCD_INK = '#FFFFFF'
/** Bus LCD dim text color. */
export const LCD_DIM = 'rgba(255,255,255,0.5)'
/** Bus LCD upcoming list label color. */
export const LCD_LIST_LABEL = '#7CC5E9'
/** Bus LCD upcoming stop name color. */
export const LCD_LIST_INK = 'rgba(255,255,255,0.78)'
/** Bus LCD alert red color. */
export const LCD_RED = LED_RED
/**
 * The LCD's own face. Both the bus screen over the farebox and the R160's destination panel
 * are backlit LCDs set in Helvetica, which is what this family clones — so this is the same
 * font the route bullets are, not the dot-matrix `LED_FONT` the rollsign strip beside it uses.
 */
export const LCD_FONT = BULLET_FONT_FAMILY
/**
 * The service name on the R160 destination panel ("Broadway Local"): a blue-white, brighter
 * and cooler than the terminus under it, which is plain `LCD_INK`. Measured off the panel
 * rather than picked — it is the one line on that display that is not white.
 */
export const LCD_SIGN_NAME = '#CBE3F7'

export const Type = {
  caption: role('xs', 'semibold', 'tight'),
  eyebrow: role('xs', 'bold', 'tight') + '; letter-spacing: 0.14em; text-transform: uppercase',
  body: role('sm', 'medium', 'normal'),
  bodyStrong: role('sm', 'bold', 'normal'),
  bodyLg: role('base', 'medium', 'normal'),
  title: role('md', 'bold', 'tight'),
  heading: role('lg', 'bold', 'tight'),
  display: role('xl', 'bold', 'tight'),
  screen: role('2xl', 'bold', 'tight'),
} as const
